import type { Role } from "@/shared/types/role";

/**
 * Translation key for a role badge / dropdown label. The actual copy
 * lives in `users.roleLabel.<ROLE>` in the locale JSON. Keeping this
 * indirection in one helper means we can add Spanish-only fallbacks
 * (or rename keys) in a single place.
 */
export function roleLabelKey(role: Role): string {
  return `users.roleLabel.${role}`;
}

/**
 * "Active" / "Deactivated" status copy lives under
 * `users.statusLabel.<active|deactivated>`. Centralizing the map keeps
 * the badge component dumb and lets the test suite pin the exact key.
 */
export function statusLabelKey(isActive: boolean): string {
  return isActive ? "users.statusLabel.active" : "users.statusLabel.deactivated";
}

/**
 * Compact, locale-aware date string for table cells.
 * - `null` / `undefined` → empty string (caller renders a placeholder).
 * - Invalid ISO → empty string (caller renders the placeholder too) so
 *   one bad row never breaks the layout.
 *
 * We use the user's `navigator.language` via `Intl` rather than carrying
 * a `date-fns` formatter — the only requirement is that the value reads
 * naturally, not pixel-perfect formatting.
 */
export function formatDateShort(value: string | null | undefined): string {
  if (!value) return "";
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}
